import React from 'react';
import { HelpIcon, TipsIcon, AutoStoriesIcon, DownloadIcon, PlayCircleIcon } from '../../utils/inlineIcons';
import { ArrowLink } from '../ArrowLink/ArrowLink';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import './InfoBanner.scss';

/**
 * InfoBanner - Informational banner card for contextual tips and definitions.
 *
 * Bordered card with a colored badge pill, heading, body text, and optional
 * action links with trailing icons. Badge variants: did-you-know, pro-tip,
 * learn-the-lingo.
 *
 * Action links are flat, indexed props (action1Text, action1Href,
 * action1Icon) reconstructed into an array internally. Supports up to 3
 * links; a slot renders only when both its action{N}Text and action{N}Href
 * are set.
 */

export type InfoBannerBadgeVariant = 'did-you-know' | 'pro-tip' | 'learn-the-lingo';

export type InfoBannerActionIcon = 'arrow-outward' | 'download' | 'play-circle';

/** A CMS action link, reconstructed from the flat props (text + href + optional icon). */
export interface InfoBannerAction {
  text: string;
  href?: string;
  icon?: InfoBannerActionIcon;
  action?: Extract<CtaAction, 'link' | 'popup'>;
  popupTargetId?: string;
}

/** Flat, per-action props for slots 1-3. */
export interface InfoBannerActionSlotProps {
  /** Action 1 */
  action1Text?: string;
  /** @polyType Url */
  action1Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Arrow Outward=arrow-outward,Download=download,Play Circle=play-circle
   * @polyHelp Choose the icon for a Link action. When Action is Popup, this field is ignored and a right arrow is shown.
   */
  action1Icon?: InfoBannerActionIcon;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Popup=popup
   * @polyLabel Action 1 Type
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  action1Action?: Extract<CtaAction, 'link' | 'popup'>;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  action1TargetPopupId?: string;

  /** Action 2 */
  action2Text?: string;
  /** @polyType Url */
  action2Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Arrow Outward=arrow-outward,Download=download,Play Circle=play-circle
   * @polyHelp Choose the icon for a Link action. When Action is Popup, this field is ignored and a right arrow is shown.
   */
  action2Icon?: InfoBannerActionIcon;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Popup=popup
   * @polyLabel Action 2 Type
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  action2Action?: Extract<CtaAction, 'link' | 'popup'>;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  action2TargetPopupId?: string;

  /** Action 3 */
  action3Text?: string;
  /** @polyType Url */
  action3Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Arrow Outward=arrow-outward,Download=download,Play Circle=play-circle
   * @polyHelp Choose the icon for a Link action. When Action is Popup, this field is ignored and a right arrow is shown.
   */
  action3Icon?: InfoBannerActionIcon;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Popup=popup
   * @polyLabel Action 3 Type
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  action3Action?: Extract<CtaAction, 'link' | 'popup'>;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  action3TargetPopupId?: string;
}

/**
 * @polySemanticUsage About this block: a content-rich editorial card that surfaces supplementary
 * knowledge, advice, or terminology relevant to the page context. Designed to educate and add
 * value — not to alert or signal urgency. Presented as a distinct white card with a labeled
 * header (icon + category name), bold headline, body copy, and optional CTA or media link.
 * Variants: "Did you know?" surfaces a lesser-known feature, capability, or fact framed as a
 * discovery moment; "Pro tip" delivers a quick, actionable best practice or insider
 * recommendation; "Learn the lingo" defines industry- or Amazon-specific terminology (purely
 * educational, no CTA).
 */
export interface InfoBannerProps extends InfoBannerActionSlotProps {
  /**
   * Badge variant determines color and icon
   * @polyLabel Badge Type
   * @polyOptions Did You Know=did-you-know,Pro Tip=pro-tip,Learn the Lingo=learn-the-lingo
   */
  badgeVariant?: InfoBannerBadgeVariant;
  /** Custom badge label (overrides the variant default) */
  badgeLabel?: string;
  /** Card heading text */
  heading: string;
  /** Plain text body content */
  body?: string;
  /**
   * Rich body content; takes precedence over `body`. Programmatic only — a
   * ReactNode can't be a flat CMS field, so authors use `body` instead.
   * @polyIgnore
   */
  children?: React.ReactNode;
  /** ARIA label for the card region; defaults to the badge label */
  ariaLabel?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

const BADGE_LABELS: Record<InfoBannerBadgeVariant, string> = {
  'did-you-know': 'Did you know?',
  'pro-tip': 'Pro tip',
  'learn-the-lingo': 'Learn the lingo',
};

const BADGE_ICONS: Record<InfoBannerBadgeVariant, React.FC<React.SVGProps<SVGSVGElement>>> = {
  'did-you-know': HelpIcon,
  'pro-tip': TipsIcon,
  'learn-the-lingo': AutoStoriesIcon,
};

const BadgeIcon: React.FC<{ variant: InfoBannerBadgeVariant }> = ({ variant }) => {
  const Icon = BADGE_ICONS[variant];
  return <Icon width="16" height="16" aria-hidden="true" />;
};

const ACTION_ICONS: Record<Exclude<InfoBannerActionIcon, 'arrow-outward'>, React.FC<React.SVGProps<SVGSVGElement>>> = {
  'download': DownloadIcon,
  'play-circle': PlayCircleIcon,
};

const ActionIcon: React.FC<{ icon: Exclude<InfoBannerActionIcon, 'arrow-outward'> }> = ({ icon }) => {
  const Icon = ACTION_ICONS[icon];
  return <Icon width="20" height="20" aria-hidden="true" />;
};

function getActions(props: InfoBannerActionSlotProps): InfoBannerAction[] {
  const out: InfoBannerAction[] = [];
  ([1, 2, 3] as const).forEach((slot) => {
    const text = props[`action${slot}Text`];
    const href = props[`action${slot}Href`];
    const action = props[`action${slot}Action`];
    const popupTargetId = props[`action${slot}TargetPopupId`];
    if (!text || !hasCtaDestination(href, action, popupTargetId)) return;
    out.push({ text, href, icon: props[`action${slot}Icon`], action, popupTargetId });
  });
  return out;
}

export const InfoBanner: React.FC<InfoBannerProps> = ({
  badgeVariant = 'did-you-know',
  badgeLabel,
  heading,
  body,
  children,
  ariaLabel,
  anchorId,
  ...slotProps
}) => {
  const resolvedLabel = badgeLabel || BADGE_LABELS[badgeVariant];
  const actions = getActions(slotProps as InfoBannerActionSlotProps);

  return (
    <section
      {...blockAnchorAttributes(anchorId)}
      className="info-banner"
      aria-label={ariaLabel || resolvedLabel}
    >
      <div className="info-banner__container">
        {/* Badge */}
        <div className={`info-banner__badge info-banner__badge--${badgeVariant}`}>
          <span className="info-banner__badge-icon">
            <BadgeIcon variant={badgeVariant} />
          </span>
          <span className="info-banner__badge-label">{resolvedLabel}</span>
        </div>

        {/* Content */}
        <div className="info-banner__content">
          <h3 className="info-banner__heading">{heading}</h3>
          <div className="info-banner__body">
            {children || <p>{body}</p>}
          </div>
        </div>

        {/* Actions */}
        {actions.length > 0 && (
          <div className="info-banner__actions">
            {actions.map((action) => {
              // Popup actions use the shared ArrowLink trigger regardless of the selected link icon.
              if (action.action === 'popup' || action.icon === 'arrow-outward') {
                return (
                  <ArrowLink
                    key={action.text}
                    direction={action.action === 'popup' ? 'right' : 'diagonal'}
                    href={action.href}
                    action={action.action}
                    popupTargetId={action.popupTargetId}
                    className="info-banner__action"
                  >
                    {action.text}
                  </ArrowLink>
                );
              }

              return (
                <a key={action.text} className="info-banner__action" href={action.href}>
                  <span className="info-banner__action-text">{action.text}</span>
                  {action.icon && (
                    <span className="info-banner__action-icon" aria-hidden="true">
                      <ActionIcon icon={action.icon} />
                    </span>
                  )}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
